/**
 * SummaryCard Component
 * Metric card with a colored left accent border for task summary counts.
 */

import styled, { css } from 'styled-components';

export interface SummaryCardProps {
  label: string;
  value: number;
  accentColor: string;
  onClick?: () => void;
  /** Optional small line under the value — use to disambiguate units (e.g. "across 5 Red Dots"). */
  subtitle?: string;
}

const CardWrapper = styled.article<{ $accentColor: string; $clickable: boolean }>`
  flex: 1;
  min-width: 140px;
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-left: 4px solid ${({ $accentColor }) => $accentColor};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[5]}`};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  transition: ${({ theme }) => theme.transitions.fast};

  ${({ $clickable, theme }) =>
    $clickable &&
    css`
      cursor: pointer;

      &:hover {
        box-shadow: ${theme.shadows.md};
        border-color: ${theme.colors.borders.medium};
        border-left-color: inherit;
        transform: translateY(-1px);
      }

      &:active {
        transform: translateY(0);
      }

      &:focus-visible {
        outline: 2px solid ${theme.colors.primary.base};
        outline-offset: 2px;
      }
    `}
`;

const Label = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};
`;

const Value = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};
`;

const Subtitle = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};
`;

export function SummaryCard({ label, value, accentColor, onClick, subtitle }: SummaryCardProps) {
  return (
    <CardWrapper
      $accentColor={accentColor}
      $clickable={!!onClick}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      <Label>{label}</Label>
      <Value>{value}</Value>
      {subtitle && <Subtitle>{subtitle}</Subtitle>}
    </CardWrapper>
  );
}

export default SummaryCard;
